import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  TextField,
  Button,
  Paper,
  Grid,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemButton,
  Divider,
  CircularProgress,
  Alert,
  Slider,
  FormHelperText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Badge,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  Movie as MovieIcon,
  Landscape as SceneIcon,
  CameraAlt as CameraIcon,
  Animation as AnimationIcon,
  Timer as TimerIcon,
  PhotoCamera as ShotIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Add as AddIcon,
  ExpandMore as ExpandIcon,
  Timeline as TimelineIcon,
  Videocam as VideocamIcon,
  AspectRatio as AspectRatioIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { StoryboardService } from '../services/storyboardService';
import { StoryboardSession, StoryboardScene, StoryboardShot, StoryboardDetails } from '../models/storyboard';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

// Utility function to format storyboard text for better readability
const formatStoryboardText = (text: string): string => {
  if (!text) return '';
  
  return text
    // Clean up markdown formatting
    .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold markdown
    .replace(/\*([^*]+)\*/g, '$1') // Remove italic markdown
    // Clean up excessive whitespace and newlines
    .replace(/\n\s*\n\s*\n+/g, '\n\n') // Reduce multiple newlines to double newlines
    .replace(/ {2,}/g, ' ') // Replace multiple spaces with single space
    .replace(/\n +/g, '\n') // Remove leading spaces after newlines
    // Format bullet points nicely
    .replace(/\n\s*\*\s+/g, '\n• ') // Convert asterisk bullets to proper bullets
    .replace(/\n\s*-\s+/g, '\n• ') // Convert dash bullets to proper bullets
    // Clean up section headers and formatting
    .replace(/([A-Z\s]+:)\s*/g, '\n$1 ') // Add proper spacing around section headers
    // Remove redundant intro text patterns
    .replace(/^.*Here's.*?:\s*/i, '') // Remove common intro text
    .replace(/^.*I can.*?:\s*/i, '') // Remove "I can help" style intros
    .trim();
};

export const StoryboardAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mainTab, setMainTab] = useState(0);
  const [resultsTab, setResultsTab] = useState(0);
  const [sessions, setSessions] = useState<StoryboardSession[]>([]);
  const [currentSession, setCurrentSession] = useState<StoryboardSession | null>(null);
  const [selectedScene, setSelectedScene] = useState<StoryboardScene | null>(null);
  
  // Initial setup state
  const [title, setTitle] = useState('');
  const [concept, setConcept] = useState('');
  const [genre, setGenre] = useState('action');
  const [style, setStyle] = useState('cinematic');
  const [duration, setDuration] = useState<number | undefined>(undefined);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  
  // Scene creation state
  const [sceneDescription, setSceneDescription] = useState('');
  const [sceneNumber, setSceneNumber] = useState<number | undefined>(undefined);
  const [sceneDuration, setSceneDuration] = useState<number | undefined>(undefined);
  
  // Additional operations state
  const [cameraDirection, setCameraDirection] = useState('');
  const [characterName, setCharacterName] = useState('');
  const [animation, setAnimation] = useState('');
  const [timingDuration, setTimingDuration] = useState(30);
  const [timingPacing, setTimingPacing] = useState('normal');
  const [numberOfShots, setNumberOfShots] = useState(5);
  
  // UI state
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operationDialog, setOperationDialog] = useState<string | null>(null);

  // Backend asset retrieval
  const {
    sessions: backendSessions,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'AnimationStoryboardSession',  // Fixed: Use SessionType not agent name
    sessionBased: true,
    autoFetch: true,
  });

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = () => {
    const allSessions = StoryboardService.getAllSessions();
    setSessions(allSessions);
  };

  // Merge backend sessions with local data
  useEffect(() => {
    const localData = StoryboardService.getAllSessions();

    if (backendSessions && backendSessions.length > 0) {
      const converted: StoryboardSession[] = backendSessions.map(bs => {
        // Parse sessionData JSON string to extract storyboard data
        let sessionData: any = {};
        try {
          if (bs.session.sessionData) {
            sessionData = JSON.parse(bs.session.sessionData);
          }
        } catch (e) {
          console.error('[StoryboardAgent] Failed to parse sessionData:', e);
        }

        return {
          id: bs.session.id,
          sessionId: bs.session.id,
          createdAt: bs.session.createdAt,
          updatedAt: bs.session.lastUpdatedAt || bs.session.updatedAt || bs.session.createdAt,
          storyboard: {
            title: sessionData.Title || bs.session.title || 'Untitled Storyboard',
            concept: sessionData.Concept || bs.session.concept || '',
            genre: sessionData.Genre || bs.session.genre || '',
            style: sessionData.Style || bs.session.style || '',
            aspectRatio: sessionData.AspectRatio || '16:9',
            estimatedDuration: sessionData.Duration || sessionData.estimatedDuration,
            scenes: sessionData.Scenes || [],
            totalShots: sessionData.Scenes?.reduce((sum: number, scene: any) => sum + (scene.Shots?.length || scene.shots?.length || 0), 0) || 0,
          }
        };
      });

      const backendIds = new Set(converted.map(s => s.id));
      const localOnly = localData.filter(s => !backendIds.has(s.id));
      setSessions([...converted, ...localOnly]);
    } else {
      setSessions(localData);
    }
  }, [backendSessions]);

  const handleStartSession = async () => {
    if (!concept.trim()) {
      setError('Please enter a storyboard concept');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await StoryboardService.startSession({
        title: title || undefined,
        concept,
        genre,
        style,
        duration,
        aspectRatio,
        temperature,
        maxTokens,
      });

      // Create initial storyboard structure from response
      const initialStoryboard: StoryboardDetails = {
        title: (response as any).title || title || 'Untitled Storyboard',
        concept: (response as any).concept || concept,
        genre: genre,
        style: style,
        aspectRatio: aspectRatio,
        estimatedDuration: duration ? `${duration}s` : undefined,
        scenes: [],
        totalShots: 0,
      };

      const session = StoryboardService.createLocalSession(
        response.sessionId,
        initialStoryboard
      );

      setCurrentSession(session);
      loadSessions();
      // Clear form
      setTitle('');
      setConcept('');
      setDuration(undefined);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create storyboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateScene = async () => {
    if (!currentSession || !sceneDescription.trim()) {
      setError('Please provide a scene description');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await StoryboardService.createScene(currentSession.sessionId, {
        sceneDescription,
        sceneNumber,
        duration: sceneDuration,
        temperature,
        maxTokens,
      });

      // Check if the response has the expected structure
      if (response.scene && response.success) {
        try {
          // Fetch the complete current storyboard from the backend using the export endpoint
          const completeStoryboard = await StoryboardService.fetchSessionFromApi(currentSession.sessionId);
          
          // Update local storage with the complete storyboard
          StoryboardService.updateStoryboard(currentSession.id, completeStoryboard);
          const updatedSession = StoryboardService.getSession(currentSession.id);
          
          setCurrentSession(updatedSession);
          
          // Refresh sessions list for History tab
          loadSessions();
          
          // Clear form
          setSceneDescription('');
          setSceneNumber(undefined);
          setSceneDuration(undefined);
          
          // Show success message
          setError(null);
        } catch (fetchError) {
          console.error('Failed to fetch updated storyboard:', fetchError);
          // Fallback to manual update
          const updatedStoryboard = { ...currentSession.storyboard };
          
          // Add or update the scene in the storyboard
          const existingSceneIndex = updatedStoryboard.scenes.findIndex(s => s.sceneNumber === response.scene.sceneNumber);
          if (existingSceneIndex >= 0) {
            updatedStoryboard.scenes[existingSceneIndex] = response.scene;
          } else {
            updatedStoryboard.scenes.push(response.scene);
          }
          
          // Sort scenes by scene number
          updatedStoryboard.scenes.sort((a, b) => a.sceneNumber - b.sceneNumber);
          
          StoryboardService.updateStoryboard(currentSession.id, updatedStoryboard);
          setCurrentSession(StoryboardService.getSession(currentSession.id));
          
          // Refresh sessions list for History tab
          loadSessions();
          
          // Clear form
          setSceneDescription('');
          setSceneNumber(undefined);
          setSceneDuration(undefined);
          
          setError(null);
        }
      } else {
        setError('Scene creation failed. Please check if a scene with this number already exists.');
      }
    } catch (err: any) {
      const errorResponse = err.response?.data;
      if (errorResponse?.errors && Array.isArray(errorResponse.errors)) {
        const errorMessage = errorResponse.errors.join(', ');
        setError(errorMessage);
        
        // If the error mentions a scene already exists, try to fetch the current session state
        if (errorMessage.includes('already exists')) {
          try {
            const updatedStoryboard = await StoryboardService.fetchSessionFromApi(currentSession.sessionId);
            StoryboardService.updateStoryboard(currentSession.id, updatedStoryboard);
            setCurrentSession(StoryboardService.getSession(currentSession.id));
            setError(`${errorMessage} The existing scenes have been loaded.`);
          } catch (fetchErr) {
            // If fetching fails, just show the original error
            console.error('Failed to fetch updated session:', fetchErr);
          }
        }
      } else {
        setError(errorResponse?.message || 'Failed to create scene');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddCameraDirection = async () => {
    if (!currentSession || !selectedScene || !cameraDirection.trim()) {
      setError('Please select a scene and provide camera direction');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await StoryboardService.addCameraDirection(currentSession.sessionId, {
        sceneId: selectedScene.id || '',
        cameraDirection,
        temperature,
        maxTokens,
      });

      const updatedScene = response.scene;
      const updatedStoryboard = { ...currentSession.storyboard };
      const sceneIndex = updatedStoryboard.scenes.findIndex(s => s.id === updatedScene.id);
      if (sceneIndex >= 0) {
        updatedStoryboard.scenes[sceneIndex] = updatedScene;
      }

      StoryboardService.updateStoryboard(currentSession.id, updatedStoryboard);
      setCurrentSession(StoryboardService.getSession(currentSession.id));
      setSelectedScene(updatedScene);
      setCameraDirection('');
      setOperationDialog(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add camera direction');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCharacterAnimation = async () => {
    if (!currentSession || !selectedScene || !characterName.trim() || !animation.trim()) {
      setError('Please provide character name and animation');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await StoryboardService.addCharacterAnimation(currentSession.sessionId, {
        sceneId: selectedScene.id || '',
        characterName,
        animation,
        temperature,
        maxTokens,
      });

      const updatedScene = response.scene;
      const updatedStoryboard = { ...currentSession.storyboard };
      const sceneIndex = updatedStoryboard.scenes.findIndex(s => s.id === updatedScene.id);
      if (sceneIndex >= 0) {
        updatedStoryboard.scenes[sceneIndex] = updatedScene;
      }

      StoryboardService.updateStoryboard(currentSession.id, updatedStoryboard);
      setCurrentSession(StoryboardService.getSession(currentSession.id));
      setSelectedScene(updatedScene);
      setCharacterName('');
      setAnimation('');
      setOperationDialog(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add character animation');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTiming = async () => {
    if (!currentSession || !selectedScene) {
      setError('Please select a scene');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await StoryboardService.addTiming(currentSession.sessionId, {
        sceneId: selectedScene.id || '',
        timing: {
          duration: timingDuration,
          pacing: timingPacing,
        },
        temperature,
        maxTokens,
      });

      const updatedScene = response.scene;
      const updatedStoryboard = { ...currentSession.storyboard };
      const sceneIndex = updatedStoryboard.scenes.findIndex(s => s.id === updatedScene.id);
      if (sceneIndex >= 0) {
        updatedStoryboard.scenes[sceneIndex] = updatedScene;
      }

      StoryboardService.updateStoryboard(currentSession.id, updatedStoryboard);
      setCurrentSession(StoryboardService.getSession(currentSession.id));
      setSelectedScene(updatedScene);
      setOperationDialog(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add timing');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShots = async () => {
    if (!currentSession || !selectedScene) {
      setError('Please select a scene');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await StoryboardService.createShots(currentSession.sessionId, {
        sceneId: selectedScene.id || '',
        numberOfShots,
        temperature,
        maxTokens,
      });

      const updatedScene = response.scene;
      const updatedStoryboard = { ...currentSession.storyboard };
      const sceneIndex = updatedStoryboard.scenes.findIndex(s => s.id === updatedScene.id);
      if (sceneIndex >= 0) {
        updatedStoryboard.scenes[sceneIndex] = updatedScene;
      }

      StoryboardService.updateStoryboard(currentSession.id, updatedStoryboard);
      setCurrentSession(StoryboardService.getSession(currentSession.id));
      setSelectedScene(updatedScene);
      setOperationDialog(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create shots');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSession = (session: StoryboardSession) => {
    setCurrentSession(session);
    setSelectedScene(null);
    setMainTab(0); // Switch back to main tab
  };

  const handleDeleteSession = (id: string) => {
    StoryboardService.deleteSession(id);
    if (currentSession?.id === id) {
      setCurrentSession(null);
      setSelectedScene(null);
    }
    loadSessions();
  };

  const handleExport = async () => {
    if (!currentSession) return;

    try {
      const response = await StoryboardService.exportStoryboard(currentSession.sessionId);
      const blob = new Blob([response.export.content || ''], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `storyboard-${(currentSession?.storyboard?.title || 'untitled').toLowerCase().replace(/\s+/g, '-')}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // Fallback to local export
      const content = StoryboardService.exportToMarkdown(currentSession.storyboard);
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `storyboard-${(currentSession?.storyboard?.title || 'untitled').toLowerCase().replace(/\s+/g, '-')}.md`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const renderSceneCard = (scene: StoryboardScene) => {
    // Handle both old and new API structures
    const description = scene.visualDescription || scene.description || 'No description available';
    const location = scene.location || 'Unknown location';
    const timeOfDay = scene.timeOfDay || 'Unknown time';
    const mood = scene.moodAndAtmosphere || scene.mood || 'Unknown mood';
    const sceneId = scene.id || `scene-${scene.sceneNumber}`;
    
    return (
      <Card 
        sx={{ 
          mb: 2, 
          cursor: 'pointer',
          border: selectedScene?.sceneNumber === scene.sceneNumber ? 2 : 0,
          borderColor: 'primary.main',
        }}
        onClick={() => setSelectedScene(scene)}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Scene {scene.sceneNumber}: {scene.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ 
            maxHeight: '120px', 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical',
            whiteSpace: 'pre-wrap'
          }}>
            {formatStoryboardText(description)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {location !== 'Unknown location' && (
              <Chip label={location} size="small" icon={<LocationIcon />} />
            )}
            {timeOfDay !== 'Unknown time' && (
              <Chip label={timeOfDay} size="small" icon={<TimeIcon />} />
            )}
            {mood !== 'Unknown mood' && (
              <Chip label={mood} size="small" color="secondary" />
            )}
            {(scene.duration || scene.targetDuration) && (
              <Chip label={`${scene.duration || scene.targetDuration}s`} size="small" />
            )}
            {scene.shots && scene.shots.length > 0 && (
              <Chip label={`${scene.shots.length} shots`} size="small" icon={<ShotIcon />} />
            )}
          </Box>
        </CardContent>
        <CardActions>
          <Tooltip title="Add Camera Direction">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOperationDialog('camera'); }}>
              <CameraIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Character Animation">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOperationDialog('animation'); }}>
              <AnimationIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Set Timing">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOperationDialog('timing'); }}>
              <TimerIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Create Shots">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOperationDialog('shots'); }}>
              <ShotIcon />
            </IconButton>
          </Tooltip>
        </CardActions>
      </Card>
    );
  };

  const renderShotDetails = (shot: StoryboardShot) => {
    return (
      <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Shot {shot.shotNumber} - {shot.shotType}
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption">Framing: {shot.framing}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption">Duration: {shot.duration}s</Typography>
          </Grid>
          {shot.angle && (
            <Grid item xs={6}>
              <Typography variant="caption">Angle: {shot.angle}</Typography>
            </Grid>
          )}
          {shot.cameraMovement && (
            <Grid item xs={6}>
              <Typography variant="caption">Movement: {shot.cameraMovement}</Typography>
            </Grid>
          )}
        </Grid>
        <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
          {formatStoryboardText(shot.description)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
          Action: {formatStoryboardText(shot.action)}
        </Typography>
        {shot.dialogue && (
          <Typography variant="body2" sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
            Dialogue: "{formatStoryboardText(shot.dialogue)}"
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: { xs: 1, sm: 2 }, 
            mb: 2,
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
            flexWrap: 'wrap'
          }}
        >
          <MovieIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'Storyboard' : 'Storyboard Agent'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile ? 'AI-powered storyboards' : 'Create professional storyboards with AI-powered scene generation'}
        </Typography>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        {/* Main Tabs */}
        <Tabs 
          value={mainTab} 
          onChange={(_, val) => setMainTab(val)} 
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 48
            }
          }}
          variant={isMobile ? 'fullWidth' : 'standard'}
        >
          <Tab label="Main" />
          <Tab label="History" />
        </Tabs>

        {/* Main Tab Panel */}
        <TabPanel value={mainTab} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
            {currentSession && (
              <>
                <IconButton 
                  onClick={async () => {
                    try {
                      const completeStoryboard = await StoryboardService.fetchSessionFromApi(currentSession.sessionId);
                      StoryboardService.updateStoryboard(currentSession.id, completeStoryboard);
                      const updatedSession = StoryboardService.getSession(currentSession.id);
                      setCurrentSession(updatedSession);
                      loadSessions();
                    } catch (err) {
                      setError('Failed to refresh storyboard from server');
                    }
                  }} 
                  title="Refresh from Server"
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <TimelineIcon />
                </IconButton>
                <IconButton 
                  onClick={handleExport} 
                  title="Export Storyboard"
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <DownloadIcon />
                </IconButton>
              </>
            )}
          </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' }
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}


        {/* Form Section - Full Width at Top */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, backgroundColor: 'background.default' }}>
          {!currentSession ? (
            <>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'Create' : 'Create Storyboard'}
              </Typography>

                  <TextField
                    fullWidth
                    label={isMobile ? 'Title (opt.)' : 'Title (optional)'}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={isMobile ? 3 : 4}
                    label={isMobile ? 'Concept' : 'Storyboard Concept'}
                    value={concept}
                    onChange={(e) => setConcept(e.target.value)}
                    placeholder={isMobile ? 'Describe your concept...' : 'Describe your animation or film concept...'}
                    sx={{ mb: 2 }}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Genre
                        </InputLabel>
                        <Select 
                          value={genre} 
                          onChange={(e) => setGenre(e.target.value)} 
                          label="Genre"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          <MenuItem value="action" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Action</MenuItem>
                          <MenuItem value="drama" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Drama</MenuItem>
                          <MenuItem value="comedy" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Comedy</MenuItem>
                          <MenuItem value="horror" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Horror</MenuItem>
                          <MenuItem value="scifi" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Sci-Fi</MenuItem>
                          <MenuItem value="fantasy" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Fantasy</MenuItem>
                          <MenuItem value="animation" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Animation</MenuItem>
                          <MenuItem value="documentary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{isMobile ? 'Doc' : 'Documentary'}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Style
                        </InputLabel>
                        <Select 
                          value={style} 
                          onChange={(e) => setStyle(e.target.value)} 
                          label="Style"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          <MenuItem value="cinematic" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Cinematic</MenuItem>
                          <MenuItem value="animation" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Animation</MenuItem>
                          <MenuItem value="comic" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{isMobile ? 'Comic' : 'Comic Book'}</MenuItem>
                          <MenuItem value="realistic" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Realistic</MenuItem>
                          <MenuItem value="stylized" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Stylized</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <FormControl fullWidth sx={{ my: 2 }}>
                    <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {isMobile ? 'Ratio' : 'Aspect Ratio'}
                    </InputLabel>
                    <Select 
                      value={aspectRatio} 
                      onChange={(e) => setAspectRatio(e.target.value)} 
                      label={isMobile ? 'Ratio' : 'Aspect Ratio'}
                      startAdornment={<AspectRatioIcon sx={{ mr: 1, color: 'action.active', fontSize: { xs: 20, sm: 24 } }} />}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      <MenuItem value="16:9" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        16:9 {!isMobile && '(Standard HD)'}
                      </MenuItem>
                      <MenuItem value="21:9" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        21:9 {!isMobile && '(Cinematic)'}
                      </MenuItem>
                      <MenuItem value="4:3" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        4:3 {!isMobile && '(Classic TV)'}
                      </MenuItem>
                      <MenuItem value="1:1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        1:1 {!isMobile && '(Square)'}
                      </MenuItem>
                      <MenuItem value="9:16" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        9:16 {!isMobile && '(Vertical)'}
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label={isMobile ? 'Duration (s)' : 'Duration (seconds, optional)'}
                    type="number"
                    value={duration || ''}
                    onChange={(e) => setDuration(e.target.value ? parseInt(e.target.value) : undefined)}
                    sx={{ mb: 2 }}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleStartSession}
                    disabled={!concept.trim() || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <PlayIcon />)}
                    sx={{ minHeight: 44 }}
                  >
                    {loading ? (isMobile ? 'Creating...' : 'Creating Storyboard...') : (isMobile ? 'Create' : 'Create Storyboard')}
                  </Button>
                </>
              ) : (
                <>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    {currentSession?.storyboard?.title || 'Untitled Storyboard'}
                  </Typography>
                  
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mb: 2,
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                    }}
                  >
                    {currentSession?.storyboard?.scenes?.length || 0} {isMobile ? 'scenes' : 'scenes created'}
                  </Alert>

                  <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

                  <Typography 
                    variant="subtitle1" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {isMobile ? 'New Scene' : 'Add New Scene'}
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={isMobile ? 2 : 3}
                    label={isMobile ? 'Scene Desc.' : 'Scene Description'}
                    value={sceneDescription}
                    onChange={(e) => setSceneDescription(e.target.value)}
                    placeholder={isMobile ? 'Describe scene...' : 'Describe the scene...'}
                    sx={{ mb: 2 }}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label={isMobile ? 'Scene #' : 'Scene Number'}
                        type="number"
                        value={sceneNumber || ''}
                        onChange={(e) => setSceneNumber(e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder={isMobile ? `(${(currentSession?.storyboard?.scenes?.length || 0) + 1})` : `Auto (next: ${(currentSession?.storyboard?.scenes?.length || 0) + 1})`}
                        InputProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                        InputLabelProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Duration (s)"
                        type="number"
                        value={sceneDuration || ''}
                        onChange={(e) => setSceneDuration(e.target.value ? parseInt(e.target.value) : undefined)}
                        InputProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                        InputLabelProps={{
                          sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ mt: 2, minHeight: 44 }}
                    onClick={handleCreateScene}
                    disabled={!sceneDescription.trim() || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <AddIcon />)}
                  >
                    {loading ? (isMobile ? 'Creating...' : 'Creating Scene...') : (isMobile ? 'Add' : 'Add Scene')}
                  </Button>

                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{ mt: 2, minHeight: 44 }}
                    onClick={() => {
                      setCurrentSession(null);
                      setSelectedScene(null);
                    }}
                  >
                    {isMobile ? 'New Storyboard' : 'Start New Storyboard'}
                  </Button>

                  <Divider sx={{ my: { xs: 2, sm: 3 } }} />

                  <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Model Settings
                  </Typography>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Typography 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {isMobile ? 'Temp' : 'Temperature'}: {temperature}
                    </Typography>
                    <Slider
                      value={temperature}
                      onChange={(_, val) => setTemperature(val as number)}
                      min={0}
                      max={1}
                      step={0.1}
                      marks
                      valueLabelDisplay="auto"
                    />
                    <FormHelperText sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      {isMobile ? '0 = focused, 1 = creative' : 'Controls creativity'}
                    </FormHelperText>
                  </FormControl>
                </>
              )}
        </Paper>

        {/* Results Section - Full Width at Bottom */}
        {currentSession && (
          <Paper sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'background.default' }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
            >
              {isMobile ? 'Results' : 'Storyboard Results'}
            </Typography>
            <>
              <Tabs 
                value={resultsTab} 
                onChange={(_, val) => setResultsTab(val)} 
                sx={{ 
                  mb: 2,
                  '& .MuiTab-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minHeight: 48
                  }
                }}
                variant={isMobile ? 'fullWidth' : 'standard'}
              >
                <Tab label="Scenes" />
                <Tab label={isMobile ? 'Selected' : 'Selected Scene'} disabled={!selectedScene} />
                <Tab label="Overview" />
              </Tabs>

              <TabPanel value={resultsTab} index={0}>
                    {(currentSession?.storyboard?.scenes?.length || 0) === 0 ? (
                      <Alert 
                        severity="info"
                        sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                      >
                        {isMobile ? 'No scenes yet' : 'No scenes created yet. Add a scene to get started.'}
                      </Alert>
                    ) : (
                      (currentSession?.storyboard?.scenes || []).map((scene) => renderSceneCard(scene))
                    )}
                  </TabPanel>

              <TabPanel value={resultsTab} index={1}>
                    {selectedScene && (
                      <Box>
                        <Typography 
                          variant="h5" 
                          gutterBottom
                          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                        >
                          {isMobile ? `S${selectedScene.sceneNumber}: ${selectedScene.title}` : `Scene ${selectedScene.sceneNumber}: ${selectedScene.title}`}
                        </Typography>

                        <Grid container spacing={{ xs: 2, sm: 3 }}>
                          <Grid item xs={12}>
                            <Card>
                              <CardContent>
                                <Typography 
                                  variant="h6" 
                                  gutterBottom
                                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                                >
                                  {isMobile ? 'Visual' : 'Visual Description'}
                                </Typography>
                                <Typography 
                                  sx={{ 
                                    whiteSpace: 'pre-wrap',
                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                  }}
                                >
                                  {formatStoryboardText(selectedScene.visualDescription || selectedScene.description || 'No description available')}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Card>
                              <CardContent>
                                <Typography 
                                  variant="h6" 
                                  gutterBottom
                                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                                >
                                  {isMobile ? 'Details' : 'Scene Details'}
                                </Typography>
                                {selectedScene.location && (
                                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    <strong>Location:</strong> {selectedScene.location}
                                  </Typography>
                                )}
                                {selectedScene.timeOfDay && (
                                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    <strong>Time:</strong> {selectedScene.timeOfDay}
                                  </Typography>
                                )}
                                {(selectedScene.mood || selectedScene.moodAndAtmosphere) && (
                                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    <strong>Mood:</strong> {selectedScene.moodAndAtmosphere || selectedScene.mood}
                                  </Typography>
                                )}
                                {selectedScene.visualStyle && (
                                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    <strong>Style:</strong> {selectedScene.visualStyle}
                                  </Typography>
                                )}
                                {(selectedScene.duration || selectedScene.targetDuration) && (
                                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    <strong>Duration:</strong> {selectedScene.duration || selectedScene.targetDuration}s
                                  </Typography>
                                )}
                                {selectedScene.createdAt && (
                                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    <strong>Created:</strong> {isMobile ? new Date(selectedScene.createdAt).toLocaleDateString() : new Date(selectedScene.createdAt).toLocaleString()}
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>

                          {selectedScene.characters.length > 0 && (
                            <Grid item xs={12}>
                              <Card>
                                <CardContent>
                                  <Typography 
                                    variant="h6" 
                                    gutterBottom
                                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                                  >
                                    Characters
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {selectedScene.characters.map((char, i) => (
                                      <Chip 
                                        key={i} 
                                        label={char}
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                      />
                                    ))}
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          )}

                          {selectedScene.cameraDirections && selectedScene.cameraDirections.length > 0 && (
                            <Grid item xs={12}>
                              <Card>
                                <CardContent>
                                  <Typography 
                                    variant="h6" 
                                    gutterBottom
                                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                                  >
                                    {isMobile ? 'Camera' : 'Camera Directions'}
                                  </Typography>
                                  {selectedScene.cameraDirections.map((dir, i) => (
                                    <Typography 
                                      key={i} 
                                      variant="body2"
                                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                    >
                                      • {dir}
                                    </Typography>
                                  ))}
                                </CardContent>
                              </Card>
                            </Grid>
                          )}

                          {selectedScene.characterAnimations && selectedScene.characterAnimations.length > 0 && (
                            <Grid item xs={12}>
                              <Card>
                                <CardContent>
                                  <Typography variant="h6" gutterBottom>Character Animations</Typography>
                                  {selectedScene.characterAnimations.map((anim, i) => (
                                    <Box key={i} sx={{ mb: 1 }}>
                                      <Typography variant="body2">
                                        <strong>{anim.characterName}:</strong> {anim.animation}
                                        {anim.timing && ` (${anim.timing})`}
                                      </Typography>
                                    </Box>
                                  ))}
                                </CardContent>
                              </Card>
                            </Grid>
                          )}

                          {selectedScene.timing && (
                            <Grid item xs={12}>
                              <Card>
                                <CardContent>
                                  <Typography variant="h6" gutterBottom>Timing</Typography>
                                  <Typography>Duration: {selectedScene.timing.duration} seconds</Typography>
                                  <Typography>Pacing: {selectedScene.timing.pacing}</Typography>
                                  {selectedScene.timing.beatPoints && selectedScene.timing.beatPoints.length > 0 && (
                                    <>
                                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Beat Points:</Typography>
                                      {selectedScene.timing.beatPoints.map((beat, i) => (
                                        <Typography key={i} variant="body2">• {beat}</Typography>
                                      ))}
                                    </>
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>
                          )}

                          {selectedScene.shots && selectedScene.shots.length > 0 && (
                            <Grid item xs={12}>
                              <Card>
                                <CardContent>
                                  <Typography variant="h6" gutterBottom>Shots</Typography>
                                  {selectedScene.shots.map((shot) => renderShotDetails(shot))}
                                </CardContent>
                              </Card>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}
                  </TabPanel>

              <TabPanel value={resultsTab} index={2}>
                    <Typography variant="h5" gutterBottom>
                      Storyboard Overview
                    </Typography>
                    
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Details</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} md={3}>
                            <Typography variant="subtitle2">Genre</Typography>
                            <Typography>{currentSession?.storyboard?.genre || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography variant="subtitle2">Style</Typography>
                            <Typography>{currentSession?.storyboard?.style || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography variant="subtitle2">Aspect Ratio</Typography>
                            <Typography>{currentSession?.storyboard?.aspectRatio || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography variant="subtitle2">Total Scenes</Typography>
                            <Typography>{currentSession?.storyboard?.scenes?.length || 0}</Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Concept</Typography>
                        <Typography>{currentSession?.storyboard?.concept || 'No concept provided'}</Typography>
                      </CardContent>
                    </Card>
                  </TabPanel>
            </>
          </Paper>
        )}
        </TabPanel>

        {/* History Tab Panel */}
        <TabPanel value={mainTab} index={1}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
          >
            {isMobile ? 'History' : 'Session History'}
          </Typography>
          
          {sessions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
              <MovieIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: 'text.secondary', mb: 2 }} />
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'No sessions' : 'No storyboard sessions yet'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {isMobile ? 'Create your first' : 'Create your first storyboard to get started'}
              </Typography>
            </Box>
          ) : (
            <List>
              {sessions.map((session) => (
                <React.Fragment key={session.id}>
                  <ListItem sx={{ pr: isMobile ? 10 : 14 }}>
                    <ListItemText
                      primary={session.storyboard?.title || 'Untitled Storyboard'}
                      secondary={isMobile 
                        ? `${new Date(session.createdAt).toLocaleDateString()} | ${session.storyboard?.scenes?.length || 0} scenes`
                        : `Created: ${new Date(session.createdAt).toLocaleString()} | ${session.storyboard?.scenes?.length || 0} scenes`
                      }
                      primaryTypographyProps={{
                        sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                      }}
                      secondaryTypographyProps={{
                        sx: { fontSize: { xs: '0.7rem', sm: '0.75rem' } }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        onClick={() => handleLoadSession(session)} 
                        title="Load Session"
                        sx={{ minWidth: 40, minHeight: 40 }}
                        size="small"
                      >
                        <PlayIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDeleteSession(session.id)} 
                        title="Delete Session"
                        sx={{ minWidth: 40, minHeight: 40 }}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>
      </Paper>

        {/* Camera Direction Dialog */}
        <Dialog 
          open={operationDialog === 'camera'} 
          onClose={() => setOperationDialog(null)} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {isMobile ? 'Camera Direction' : 'Add Camera Direction'}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Camera Direction"
              value={cameraDirection}
              onChange={(e) => setCameraDirection(e.target.value)}
              placeholder={isMobile ? 'e.g., Slow pan left to right...' : 'e.g., Slow pan from left to right revealing the landscape...'}
              sx={{ mt: 2 }}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setOperationDialog(null)}
              sx={{ minHeight: 44 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddCameraDirection} 
              disabled={!cameraDirection.trim() || loading}
              startIcon={loading && <CircularProgress size={20} />}
              sx={{ minHeight: 44 }}
            >
              {isMobile ? 'Add' : 'Add Direction'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Character Animation Dialog */}
        <Dialog 
          open={operationDialog === 'animation'} 
          onClose={() => setOperationDialog(null)} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {isMobile ? 'Character Animation' : 'Add Character Animation'}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={isMobile ? 'Character' : 'Character Name'}
              value={characterName}
              onChange={(e) => setCharacterName(e.target.value)}
              sx={{ mt: 2, mb: 2 }}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label={isMobile ? 'Animation' : 'Animation Description'}
              value={animation}
              onChange={(e) => setAnimation(e.target.value)}
              placeholder="e.g., Jumps excitedly with arms raised..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOperationDialog(null)}>Cancel</Button>
            <Button 
              onClick={handleAddCharacterAnimation} 
              disabled={!characterName.trim() || !animation.trim() || loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Add Animation
            </Button>
          </DialogActions>
        </Dialog>

        {/* Timing Dialog */}
        <Dialog open={operationDialog === 'timing'} onClose={() => setOperationDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Set Scene Timing</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <Typography gutterBottom>Duration: {timingDuration} seconds</Typography>
              <Slider
                value={timingDuration}
                onChange={(_, val) => setTimingDuration(val as number)}
                min={5}
                max={300}
                step={5}
                marks
                valueLabelDisplay="auto"
              />
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Pacing</InputLabel>
              <Select value={timingPacing} onChange={(e) => setTimingPacing(e.target.value)} label="Pacing">
                <MenuItem value="slow">Slow</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="fast">Fast</MenuItem>
                <MenuItem value="variable">Variable</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOperationDialog(null)}>Cancel</Button>
            <Button 
              onClick={handleAddTiming} 
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Set Timing
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Shots Dialog */}
        <Dialog open={operationDialog === 'shots'} onClose={() => setOperationDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Create Scene Shots</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <Typography gutterBottom>Number of Shots: {numberOfShots}</Typography>
              <Slider
                value={numberOfShots}
                onChange={(_, val) => setNumberOfShots(val as number)}
                min={1}
                max={20}
                marks
                valueLabelDisplay="auto"
              />
              <FormHelperText>
                Each shot will include framing, camera movement, and action details
              </FormHelperText>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOperationDialog(null)}>Cancel</Button>
            <Button 
              onClick={handleCreateShots} 
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Create Shots
            </Button>
          </DialogActions>
        </Dialog>
    </Container>
  );
};